import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { Plus, CardsThree, BookOpen, Trash, Play, ArrowRight, GraduationCap, Calendar, GearSix, X, Flame, Target as TargetIcon, CheckCircle, Circle, Headphones } from "@phosphor-icons/react";
import { toast } from "sonner";
import useStats from "../lib/useStats";
import DailyProgressRing from "../components/DailyProgressRing";
import MasteryBadge from "../components/MasteryBadge";

const WELCOME_DISMISS_KEY = "ekkol_welcome_nudge_dismissed";

export default function Dashboard() {
  const [decks, setDecks] = useState([]);
  const [assignments, setAssignments] = useState([]); // upcoming from enrolled classes
  const [mastery, setMastery] = useState({}); // {deck_id: {level, pct}}
  const [loading, setLoading] = useState(true);
  const { stats } = useStats();
  const [showWelcome, setShowWelcome] = useState(() => {
    try { return localStorage.getItem(WELCOME_DISMISS_KEY) !== "1"; } catch { return false; }
  });
  const dismissWelcome = () => {
    try { localStorage.setItem(WELCOME_DISMISS_KEY, "1"); } catch {}
    setShowWelcome(false);
  };
  const navigate = useNavigate();

  // Onboarding signals tracked here so the new-user checklist can show
  // accurate progress. `hasStudied` is derived from mastery further below.
  const [prefsCheck, setPrefsCheck] = useState({ hasVoice: false, loaded: false });

  const load = async () => {
    try {
      const { data } = await api.get("/decks");
      setDecks(data.decks || []);
    } catch (e) {
      toast.error("Could not load your decks");
    } finally {
      setLoading(false);
    }
    // Pull per-deck mastery (best-effort)
    try {
      const { data } = await api.get("/me/deck-mastery");
      setMastery(data?.decks || {});
    } catch { /* non-fatal */ }
    // Onboarding signals — has the user picked a voice? have they completed
    // any study session? Both feed the new-user checklist below. Failures
    // here just leave checklist items unchecked, which is safe.
    try {
      const { data: prefs } = await api.get("/users/me/prefs");
      setPrefsCheck((p) => ({
        ...p,
        hasVoice: !!(prefs?.voice_uri || prefs?.voiceURI),
        loaded: true,
      }));
    } catch {
      setPrefsCheck((p) => ({ ...p, loaded: true }));
    }
    // Pull any active assignments from classes the user is enrolled in (best-effort, non-blocking)
    try {
      const { data: cls } = await api.get("/classes/mine");
      const enrolled = cls.enrolled || [];
      const all = [];
      // Fetch first 3 enrolled class details in parallel to get assignments
      await Promise.all(
        enrolled.slice(0, 5).map(async (c) => {
          try {
            const { data: d } = await api.get(`/classes/${c.class_id}`);
            (d.assignments || []).forEach((a) => all.push({ ...a, class_name: c.name }));
          } catch {}
        }),
      );
      // Sort by due date first (nulls last), then created_at
      all.sort((a, b) => {
        const ad = a.due_at ? new Date(a.due_at).getTime() : Infinity;
        const bd = b.due_at ? new Date(b.due_at).getTime() : Infinity;
        return ad - bd;
      });
      setAssignments(all.slice(0, 5));
    } catch { /* silent — not everyone has classes */ }
  };

  useEffect(() => { load(); }, []);

  const del = async (id, title) => {
    console.log("[delDeck] attempting", id);
    // Use toast confirm (works even if browser blocks window.confirm)
    toast(`Delete "${title || "this deck"}" and all its cards?`, {
      duration: 8000,
      action: {
        label: "Delete",
        onClick: async () => {
          try {
            const res = await api.delete(`/decks/${id}`);
            console.log("[delDeck] success", res.status);
            setDecks((d) => d.filter((x) => x.deck_id !== id));
            toast.success("Deck removed");
          } catch (e) {
            console.error("[delDeck] FAILED:", e);
            toast.error(`Failed: ${e?.response?.data?.detail || e?.message || "Delete failed"}`);
          }
        },
      },
      cancel: {
        label: "Cancel",
        onClick: () => {},
      },
    });
  };

  // Onboarding-checklist completion. Computed during render so it stays
  // perfectly in sync with mastery / decks state without extra effects.
  const hasStudied = Object.values(mastery).some((m) => (m?.correct_cards || 0) > 0);
  const realDecks = decks.filter((d) => (d.card_count || 0) > 0);
  const hasOwnDeck = realDecks.length >= 2; // welcome deck (auto-seeded) + ≥1 of theirs
  const onboardingDone = hasOwnDeck && hasStudied && prefsCheck.hasVoice;

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-16 fade-in">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <div className="eyebrow mb-4">Your library</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
              What shall we<br/>memorize today?
            </h1>
          </div>
          <Link to="/decks/new" className="btn-primary" data-testid="new-deck-btn">
            <Plus size={18} weight="bold" /> New deck
          </Link>
        </div>

        {/* Gamification strip — streak + daily goal (only if user has studied at least once OR picked a goal) */}
        {stats && (stats.streak > 0 || stats.cards_today > 0 || stats.daily_goal) && (
          <section
            className="mb-10 border rule bg-white p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8 fade-in"
            data-testid="gamification-strip"
          >
            <div className="flex items-center gap-5 flex-1 min-w-0">
              <DailyProgressRing cards={stats.cards_today || 0} goal={stats.daily_goal || 10} testId="dashboard-daily-ring" />
              <div className="min-w-0">
                <div className="eyebrow flex items-center gap-2">
                  <TargetIcon size={12} /> Today&apos;s goal
                </div>
                <div className="font-serif text-xl sm:text-2xl leading-tight mt-0.5">
                  {stats.cards_today >= stats.daily_goal
                    ? "Goal met — see you tomorrow 👋"
                    : stats.cards_today > 0
                      ? `${stats.daily_goal - stats.cards_today} more card${stats.daily_goal - stats.cards_today === 1 ? "" : "s"} to hit today`
                      : `${stats.daily_goal} cards keeps the streak alive`}
                </div>
                <Link to="/profile#streak" className="text-xs link-underline mt-1 inline-block" data-testid="link-change-goal">
                  Change goal
                </Link>
              </div>
            </div>

            <div className="flex items-center gap-3 border-l-0 sm:border-l rule sm:pl-8 pt-3 sm:pt-0 border-t sm:border-t-0 w-full sm:w-auto">
              <div className="w-11 h-11 rounded-full bg-orange-50 flex items-center justify-center">
                <Flame size={20} weight={stats.at_risk ? "regular" : "fill"} className={stats.at_risk ? "text-[color:var(--vermilion)]" : "text-orange-500"} />
              </div>
              <div>
                <div className="eyebrow">
                  {stats.streak > 0 ? `${stats.streak}-day streak` : "Start a streak"}
                </div>
                <div className="text-xs text-[color:var(--ink-muted)] leading-snug">
                  {stats.at_risk
                    ? "Streak is at risk — study today to keep it."
                    : stats.streak > 0
                      ? `Longest: ${stats.longest_streak} days${stats.freezes ? ` · ${stats.freezes}🧊 banked` : ""}`
                      : "Study any deck today to light the flame."}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* New-user onboarding checklist. Auto-hides once all three steps
            are done OR the user dismisses it. Each step deep-links to the
            page that fulfills it, so users don't have to hunt around. */}
        {showWelcome && !onboardingDone && prefsCheck.loaded && (
          <div
            className="border rule bg-white px-5 py-5 mb-8 fade-in"
            data-testid="onboarding-checklist"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="eyebrow mb-1 text-[color:var(--oxford)]">First steps</div>
                <p className="font-serif text-lg leading-tight">Welcome to Ekkol — let&apos;s get you up and running.</p>
              </div>
              <button
                onClick={dismissWelcome}
                className="text-[color:var(--ink-faint)] hover:text-[color:var(--ink)] flex-shrink-0"
                aria-label="Dismiss"
                data-testid="btn-welcome-dismiss"
              >
                <X size={16}/>
              </button>
            </div>
            <ul className="mt-4 space-y-2.5" data-testid="onboarding-steps">
              {/* Step 1 — make their first deck */}
              <li className="flex items-start gap-3" data-testid={`step-deck-${hasOwnDeck ? "done" : "todo"}`}>
                {hasOwnDeck
                  ? <CheckCircle size={18} weight="fill" className="text-[color:var(--success)] mt-0.5 flex-shrink-0"/>
                  : <Circle size={18} className="text-[color:var(--ink-muted)] mt-0.5 flex-shrink-0"/>
                }
                <div className="flex-1 min-w-0">
                  <div className={hasOwnDeck ? "text-sm text-[color:var(--ink-muted)] line-through" : "text-sm font-medium"}>
                    Create your first deck
                  </div>
                  {!hasOwnDeck && (
                    <Link to="/decks/new" className="text-xs link-underline text-[color:var(--oxford)]" data-testid="step-deck-link">
                      Start a deck — paste notes or pick a topic →
                    </Link>
                  )}
                </div>
              </li>
              {/* Step 2 — actually run a session */}
              <li className="flex items-start gap-3" data-testid={`step-study-${hasStudied ? "done" : "todo"}`}>
                {hasStudied
                  ? <CheckCircle size={18} weight="fill" className="text-[color:var(--success)] mt-0.5 flex-shrink-0"/>
                  : <Circle size={18} className="text-[color:var(--ink-muted)] mt-0.5 flex-shrink-0"/>
                }
                <div className="flex-1 min-w-0">
                  <div className={hasStudied ? "text-sm text-[color:var(--ink-muted)] line-through" : "text-sm font-medium"}>
                    Try a study session
                  </div>
                  {!hasStudied && decks.length > 0 && (
                    <Link
                      to={`/study/${decks[0].deck_id}?mode=learn`}
                      className="text-xs link-underline text-[color:var(--oxford)]"
                      data-testid="step-study-link"
                    >
                      <Headphones size={11} className="inline mr-1"/>
                      Open the welcome deck and tap Learn →
                    </Link>
                  )}
                </div>
              </li>
              {/* Step 3 — set a voice they like */}
              <li className="flex items-start gap-3" data-testid={`step-voice-${prefsCheck.hasVoice ? "done" : "todo"}`}>
                {prefsCheck.hasVoice
                  ? <CheckCircle size={18} weight="fill" className="text-[color:var(--success)] mt-0.5 flex-shrink-0"/>
                  : <Circle size={18} className="text-[color:var(--ink-muted)] mt-0.5 flex-shrink-0"/>
                }
                <div className="flex-1 min-w-0">
                  <div className={prefsCheck.hasVoice ? "text-sm text-[color:var(--ink-muted)] line-through" : "text-sm font-medium"}>
                    Pick a voice you like
                  </div>
                  {!prefsCheck.hasVoice && (
                    <Link to="/profile" className="text-xs link-underline text-[color:var(--oxford)]" data-testid="step-voice-link">
                      <GearSix size={11} className="inline mr-1"/>
                      Profile → Voice & speed →
                    </Link>
                  )}
                </div>
              </li>
            </ul>
            <p className="mt-4 text-[11px] text-[color:var(--ink-faint)] font-mono uppercase tracking-widest">
              Steps complete themselves as you go · this card disappears when you&apos;re done
            </p>
          </div>
        )}

        {/* Enrolled-student assignment banner */}
        {assignments.length > 0 && (
          <section className="mb-12" data-testid="assignments-banner">
            <div className="flex items-center gap-2 eyebrow mb-4 text-[color:var(--oxford)]">
              <GraduationCap size={14}/> From your classes
            </div>
            <div className="border rule bg-white">
              <div className="px-5 py-3 bg-[color:var(--surface-alt)] border-b rule flex items-center justify-between">
                <span className="text-xs font-mono uppercase tracking-widest text-[color:var(--ink-muted)]">
                  {assignments.length} open assignment{assignments.length === 1 ? "" : "s"}
                </span>
                <Link to="/classes" className="text-xs link-underline">View classes</Link>
              </div>
              <div className="divide-y rule">
                {assignments.map((a) => {
                  const overdue = a.due_at && new Date(a.due_at).getTime() < Date.now();
                  const due = a.due_at ? new Date(a.due_at) : null;
                  return (
                    <div
                      key={a.assignment_id}
                      className="p-4 sm:p-5 flex items-start justify-between gap-4"
                      data-testid={`assignment-row-${a.assignment_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-serif text-lg leading-tight truncate">{a.title}</div>
                        <div className="text-xs text-[color:var(--ink-muted)] mt-1 flex flex-wrap items-center gap-3">
                          <span>{a.class_name}</span>
                          <span>·</span>
                          <span>{a.deck_card_count || 0} cards</span>
                          {due && (
                            <>
                              <span>·</span>
                              <span className={`inline-flex items-center gap-1 ${overdue ? "text-[color:var(--vermilion)]" : ""}`}>
                                <Calendar size={11}/> {overdue ? "Overdue" : "Due"} {due.toLocaleDateString()}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <Link
                        to={`/study/${a.deck_id}`}
                        className="btn-primary py-2 px-4 text-sm"
                        data-testid={`btn-study-assignment-${a.assignment_id}`}
                      >
                        <Play size={14} weight="fill"/> Practice
                      </Link>
                    </div>
                  );
                })}
              </div>
              <div className="px-5 py-3 bg-[color:var(--surface-alt)] text-xs text-[color:var(--ink-muted)]">
                💡 <strong>Tip:</strong> Before practicing, review your class notes or textbook first — Ekkol helps you <em>remember</em> what you&apos;ve learned.
              </div>
            </div>
          </section>
        )}

        {loading ? (
          <div className="wave"><span/><span/><span/><span/><span/></div>
        ) : decks.length === 0 ? (
          <div className="border rule bg-white p-12 text-center">
            <div className="eyebrow mb-4">Empty library</div>
            <h2 className="font-serif text-3xl">Create your first deck</h2>
            <p className="text-[color:var(--ink-muted)] mt-3 max-w-md mx-auto">
              Paste a script to rehearse, or drop in a bank of multiple-choice
              questions — we'll turn them into statement flashcards.
            </p>
            <div className="mt-8 flex justify-center gap-4 flex-wrap">
              <button
                onClick={() => navigate("/decks/new?mode=script")}
                className="btn-ghost"
                data-testid="empty-cta-script"
              >
                <BookOpen size={18} /> Script / text
              </button>
              <button
                onClick={() => navigate("/decks/new?mode=qa")}
                className="btn-primary"
                data-testid="empty-cta-qa"
              >
                <CardsThree size={18} /> Q & A flashcards
              </button>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[color:var(--rule)] border rule" data-testid="decks-grid">
            {decks.map((d) => (
              <div key={d.deck_id} className="bg-white p-8 group flex flex-col" data-testid={`deck-card-${d.deck_id}`}>
                <div className="eyebrow flex items-center justify-between">
                  <span>{d.mode === "qa" ? "Q & A" : "Script"}</span>
                  <span className="font-mono">{d.card_count} cards</span>
                </div>
                <h3 className="font-serif text-2xl mt-4 leading-snug flex-1">{d.title}</h3>
                {d.description && (
                  <p className="text-sm text-[color:var(--ink-muted)] mt-2 line-clamp-3">{d.description}</p>
                )}
                {mastery[d.deck_id] && (
                  <div className="mt-3">
                    <MasteryBadge
                      level={mastery[d.deck_id].level}
                      pct={mastery[d.deck_id].pct}
                      testId={`deck-mastery-${d.deck_id}`}
                    />
                  </div>
                )}
                <div className="flex items-center justify-between mt-8 pt-6 border-t rule">
                  <Link
                    to={`/decks/${d.deck_id}`}
                    className="text-sm link-underline"
                    data-testid={`deck-open-${d.deck_id}`}
                  >
                    Open deck <ArrowRight size={14} className="inline" />
                  </Link>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => del(d.deck_id, d.title)}
                      className="inline-flex items-center gap-1 text-xs text-[color:var(--vermilion)] hover:text-white hover:bg-[color:var(--vermilion)] border border-[color:var(--vermilion)]/40 hover:border-[color:var(--vermilion)] rounded px-2.5 py-1.5 transition-colors"
                      aria-label="Delete deck"
                      title="Delete deck"
                      data-testid={`deck-delete-${d.deck_id}`}
                    >
                      <Trash size={14} weight="bold" /> <span className="hidden sm:inline">Delete</span>
                    </button>
                    {d.card_count > 0 && (
                      <Link
                        to={`/study/${d.deck_id}?mode=learn`}
                        className="btn-primary py-2 px-4 text-sm"
                        data-testid={`deck-study-${d.deck_id}`}
                        title="Start in Learn mode — you can switch to Practice anytime"
                      >
                        <Play size={14} weight="fill" /> Study
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
